import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { RemindersBanner } from "@/components/Reminders";
import { PortalChecklist } from "@/components/PortalChecklist";
import { Building2, FileCheck2, Clock, CheckCircle2, AlertTriangle, FileText, Upload, GitCompare, ArrowRight } from "lucide-react";

function Tile({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 fade-up">
      <div className="flex items-center justify-between">
        <span className="small-label">{label}</span>
        <Icon className={`h-4 w-4 ${tone}`} />
      </div>
      <div className="mt-3 font-head text-3xl font-bold font-mono">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-rose-400 font-mono">{err}</div>;
  if (!data) return <div className="text-muted-foreground font-mono">Loading dashboard…</div>;

  const t = data.totals;
  return (
    <div className="space-y-8 max-w-7xl">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight">Compliance Dashboard</h1>
        <p className="text-muted-foreground mt-1">Filing status and due dates across every client GSTIN.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-tiles">
        <Tile icon={Building2} label="Clients" value={t.clients} tone="text-indigo-400" />
        <Tile icon={FileCheck2} label="Returns" value={t.returns} tone="text-cyan-400" />
        <Tile icon={CheckCircle2} label="Filed" value={t.filed} tone="text-emerald-400" />
        <Tile icon={Clock} label="Pending Approval" value={t.pending_approval} tone="text-amber-400" />
      </div>

      <RemindersBanner />

      <PortalChecklist />

      {data.pending_approvals.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <span className="font-head font-semibold text-amber-300">Awaiting partner approval</span>
          </div>
          <div className="space-y-2">
            {data.pending_approvals.map((r) => (
              <button key={r.id} data-testid={`pending-approval-${r.id}`}
                onClick={() => nav(`/clients/${r.client_id}`)}
                className="w-full flex items-center justify-between text-left rounded-md border border-border bg-card px-4 py-2.5 hover:border-amber-500/40 transition-colors">
                <span className="font-mono text-sm">{r.return_type} · {r.period}</span>
                <StatusBadge status={r.status} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <h2 className="font-head text-xl font-semibold">Client workspaces</h2>
          <p className="text-sm text-muted-foreground mt-1">Open a client to prepare & file GSTR-1 / GSTR-3B, import sales/purchase data, and reconcile GSTR-2B. Every action is audited.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.clients.map((c) => (
            <div key={c.client_id} data-testid={`dashboard-client-${c.client_id}`}
              className="rounded-lg border border-border bg-card p-5 hover:border-indigo-500/40 transition-colors flex flex-col">
              <div className="font-head font-semibold">{c.legal_name}</div>
              <div className="font-mono text-xs text-muted-foreground mt-0.5">{c.gstin}</div>
              <div className="mt-4 space-y-2 flex-1">
                {c.returns.length === 0 && <div className="text-xs text-muted-foreground">No returns prepared yet.</div>}
                {c.returns.map((r) => (
                  <div key={`${r.return_type}-${r.period}`} className="flex items-center justify-between text-xs">
                    <span className="font-mono">{r.return_type} · {r.period}</span>
                    <div className="flex items-center gap-2">
                      {r.due_date && <span className="text-muted-foreground font-mono">due {r.due_date}</span>}
                      <StatusBadge status={r.status} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-border space-y-1.5" data-testid={`dashboard-annual-${c.client_id}`}>
                <div className="small-label">Annual returns</div>
                {c.annual.map((a) => (
                  <button key={`${a.return_type}-${a.key}`} data-testid={`annual-status-${c.client_id}-${a.return_type}-${a.key}`}
                    onClick={() => nav(`/clients/${c.client_id}?tab=annual`)}
                    className="w-full flex flex-wrap items-center justify-between gap-x-2 text-xs hover:bg-secondary/50 rounded px-1 -mx-1 py-0.5 transition-colors">
                    <span className="font-mono whitespace-nowrap">{a.label}</span>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono whitespace-nowrap ${a.past_due && a.status !== "filed" ? "text-rose-400" : "text-muted-foreground"}`}>{a.past_due && a.status !== "filed" ? "overdue" : "due"} {a.due_date}</span>
                      <StatusBadge status={a.status === "draft" ? "not_started" : a.status} />
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-border grid grid-cols-3 gap-1.5">
                <button data-testid={`client-${c.client_id}-returns`} onClick={() => nav(`/clients/${c.client_id}?tab=returns`)}
                  className="flex flex-col items-center gap-1 rounded-md py-2 text-[10px] font-mono uppercase tracking-wide text-muted-foreground hover:text-indigo-300 hover:bg-secondary/60 transition-colors">
                  <FileText className="h-4 w-4" />Returns</button>
                <button data-testid={`client-${c.client_id}-import`} onClick={() => nav(`/clients/${c.client_id}?tab=import`)}
                  className="flex flex-col items-center gap-1 rounded-md py-2 text-[10px] font-mono uppercase tracking-wide text-muted-foreground hover:text-indigo-300 hover:bg-secondary/60 transition-colors">
                  <Upload className="h-4 w-4" />Import</button>
                <button data-testid={`client-${c.client_id}-reconcile`} onClick={() => nav(`/clients/${c.client_id}?tab=reconcile`)}
                  className="flex flex-col items-center gap-1 rounded-md py-2 text-[10px] font-mono uppercase tracking-wide text-muted-foreground hover:text-indigo-300 hover:bg-secondary/60 transition-colors">
                  <GitCompare className="h-4 w-4" />GSTR-2B</button>
              </div>
              <button data-testid={`open-workspace-${c.client_id}`} onClick={() => nav(`/clients/${c.client_id}`)}
                className="mt-2 flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 font-medium">
                Open workspace <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
